import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAlerts,
  setErrorAlert,
  setSuccessAlert,
} from "../../../store/alert/alertSlice";
import { postDataAPI } from "../../../utils/fetchData";
import dizeeLogo from "../../../assets/images/dizee logo.svg";
import TypeWriter from "../../../components/OnBoard/Typewriter";
import styled from "styled-components";
import AccessibilityWidget from "../../../components/AccessibilityWidget";
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-top: 0;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
  margin-top: -40px;
  height: 100%;
  position: relative;
  padding-top: 40px;
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 0;
  width: 280px;
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  display: flex;
  align-items: center;
  position: relative;
`;

const ActionButton = styled.div`
  font-size: 12px;
  color: white;
  cursor: pointer;
  height: 18px;
  line-height: 18px;
  position: relative;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
`;

// Role display names
const ROLE_DISPLAY_NAMES = {
  'manager': 'Manager',
  'support': 'Support',
  'agent': 'Agent',
  'label': 'Label',
  'publisher': 'Publisher',
  'business-manager': 'Business Manager'
};

const Step16 = ({ setStep, setUser, user }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [availableArtists, setAvailableArtists] = useState([]);
  const [selectedArtists, setSelectedArtists] = useState([]); // Changed to array for multiple selections
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // New loading state for initial load
  const [manualEmail, setManualEmail] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const { userData } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  // Check if user has Pro plan (unlimited teams)
  const isProPlan = user?.teamMemberPlan === 'pro';
  const maxSelections = isProPlan ? Infinity : 1;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);


  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  // Use pre-fetched data from step15 if available, otherwise fetch
  useEffect(() => {
    if (user?.availableArtists) {
      console.log('✅ Using pre-fetched artists:', user.availableArtists.length);
      setAvailableArtists(user.availableArtists);
      setInitialLoading(false);
    } else {
      // Fallback: fetch if no pre-fetched data
      const fetchInvitedArtists = async () => {
        setLoading(true);
        try {
          const url = new URL(window.location.href);
          const emailParam = url.searchParams.get("email");
          const teamMemberEmail = emailParam || userData?.email;

          const response = await postDataAPI("team/invited-artists", {
            teamMemberEmail: teamMemberEmail
          });

          if (response.data?.status === 200) {
            setAvailableArtists(response.data.data || []);
            console.log('✅ Available artists found:', response.data.data?.length || 0);
          } else {
            console.warn("Failed to fetch invited artists:", response.data?.message);
          }
        } catch (error) {
          console.error("Error fetching invited artists:", error);
          dispatch(setErrorAlert("Failed to load available artists"));
        } finally {
          setInitialLoading(false);
          setLoading(false);
        }
      };

      fetchInvitedArtists();
    }
  }, [user?.availableArtists, userData?.email, dispatch]);

  const checkManualEmail = async () => {
    if (!manualEmail.trim()) return;
    
    setLoading(true);
    try {
      console.log('🔍 Manually checking email:', manualEmail);
      
      const response = await postDataAPI("team/invited-artists", {
        teamMemberEmail: manualEmail.trim()
      });

      console.log('📨 Manual check response:', response.data);

      if (response.data?.status === 200) {
        setAvailableArtists(response.data.data || []);
        console.log('✅ Manual check - Available artists found:', response.data.data?.length || 0);
        if (response.data.data?.length > 0) {
          setShowManualInput(false);
        }
      } else {
        console.warn("Manual check failed:", response.data?.message);
        dispatch(setErrorAlert("No invitations found for this email"));
      }
    } catch (error) {
      console.error("Manual check error:", error);
      dispatch(setErrorAlert("Failed to check email"));
    } finally {
      setLoading(false);
    }
  };

  const handleJoinArtist = async () => {
    if (!selectedArtists.length || loading) return;

    setLoading(true);
    dispatch(clearAlerts());

    try {
      const url = new URL(window.location.href);
      const emailParam = url.searchParams.get("email");
      const teamMemberEmail = emailParam || userData?.email;

      const response = await postDataAPI("team/join-artist", {
        teamMemberEmail: teamMemberEmail,
        artistIds: selectedArtists.map(artist => artist._id),
        teamMemberPlan: user.teamMemberPlan
      });

      if (response.data?.status === 200) {
        const { joinedTeams, errors } = response.data.data;
        
        if (joinedTeams?.length > 0) {
          const artistNames = joinedTeams.map(team => team.artist.username || team.artist.email).join(', ');
          dispatch(setSuccessAlert(`Successfully joined ${artistNames}'s team${joinedTeams.length > 1 ? 's' : ''}!`));
          
          // Show any partial errors if present
          if (errors?.length > 0) {
            console.warn("Some teams could not be joined:", errors);
          }
          
          // Update user data with selected artists
          setUser({
            ...user,
            selectedArtists: joinedTeams.map(team => team.artist),
            joinedArtist: true
          });

          // Navigate to step17 for account creation and team selection
          setTimeout(() => {
            setStep(17);
          }, 1500);
        } else {
          dispatch(setErrorAlert(errors?.join(', ') || "Failed to join any teams"));
        }
      } else {
        dispatch(setErrorAlert(response.data?.message || "Failed to join artist team"));
      }
    } catch (error) {
      console.error("Error joining artist:", error);
      dispatch(setErrorAlert("Failed to join artist team"));
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getRoleDisplayName = () => {
    return ROLE_DISPLAY_NAMES[user?.professionRole] || 'Team Member';
  };

  return (
    <Container>
      <FormSection>
        <TypeWriter text={"Choose an artist"} speed={45} />
        
        <SearchSection>
          {availableArtists.length > 0 ? (
            <div>
              {/* Plan limitation messaging */}
              <div className="text-white/70 text-[10px] mb-3">
                {isProPlan ? 
                  "Pro Plan: Select multiple artists to join their teams" : 
                  "Basic Plan: Select one artist to join their team"
                }
              </div>
              
              {/* Artist Dropdown - Using Dizee Design Language */}
              <div className="relative w-[280px] dropdown-container">
                <div 
                  className="bg-black rounded-[4px] pr-4 py-3 cursor-pointer flex justify-between items-center text-white text-[12px] transition-all duration-200"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span>
                    {selectedArtists.length > 0 ? (
                      selectedArtists.length === 1 ? 
                        selectedArtists[0].name :
                        `${selectedArtists[0].name} + ${selectedArtists.length - 1} more`
                    ) : (
                      isProPlan ? "Select artists..." : "Select an artist..."
                    )}
                  </span>
                  <div 
                    className="w-0 h-0 transition-transform duration-200"
                    style={{
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '4px solid rgba(255, 255, 255, 0.7)',
                      transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  />
                </div>
                
                {dropdownOpen && (
                  <div 
                    className="absolute top-full left-0 right-0 bg-[#111111] rounded-[4px] mt-1 z-50 overflow-hidden shadow-lg max-h-[300px] overflow-y-auto"
                  >
                    <div className="h-2 bg-[#111111]"></div>
                    <div className="px-[5px] flex flex-col gap-y-[10px]">
                      {availableArtists.map((artist) => {
                        const isSelected = selectedArtists.some(selected => selected._id === artist._id);
                        const canSelect = isProPlan || selectedArtists.length === 0 || isSelected;
                        
                        return (
                          <div
                            key={artist._id}
                            className={`flex flex-col gap-y-1 cursor-pointer p-3 rounded transition-all duration-200 border ${
                              isSelected
                                ? 'border-[#FF2300]' 
                                : canSelect
                                ? 'border-transparent hover:border-[#FF2300]'
                                : 'border-transparent opacity-50 cursor-not-allowed'
                            }`}
                            onClick={() => {
                              if (!canSelect) return;
                              
                              if (isProPlan) {
                                // Pro plan: toggle selection, keep dropdown open
                                setSelectedArtists(prev => {
                                  const newSelected = [...prev];
                                  const index = newSelected.findIndex(selected => selected._id === artist._id);
                                  if (index > -1) {
                                    newSelected.splice(index, 1);
                                  } else {
                                    newSelected.push(artist);
                                  }
                                  return newSelected;
                                });
                              } else {
                                // Basic plan: single selection, close dropdown
                                setSelectedArtists([artist]);
                                setDropdownOpen(false);
                              }
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-bold overflow-hidden">
                                {artist.profilePicture ? (
                                  <img 
                                    src={artist.profilePicture} 
                                    alt={artist.name}
                                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  getInitials(artist.name)
                                )}
                              </div>
                              <div className="flex flex-col flex-1">
                                <div className="text-white text-[12px] font-semibold flex items-center gap-2">
                                  {artist.name}
                                  {isSelected && <div className="w-2 h-2 bg-[#FF2300] rounded-full"></div>}
                                </div>
                                <div className="text-white/70 text-[10px]">{artist.email}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {isProPlan && selectedArtists.length > 0 && (
                      <div className="px-4 py-2 border-t border-white/10">
                        <div className="text-white/60 text-[10px]">
                          {selectedArtists.length} team{selectedArtists.length !== 1 ? 's' : ''} selected
                        </div>
                      </div>
                    )}
                    <div className="h-2 bg-[#111111]"></div>
                  </div>
                )}
              </div>
            </div>
          ) : initialLoading ? (
            <div className="text-white text-[12px] leading-[1.4] mt-[20px] text-center">
              <div className="mb-3">
                Looking for team invitations...
              </div>
            </div>
          ) : (
            <div className="text-white text-[12px] leading-[1.4] mt-[20px]">
              <div className="mb-3">
                No artists have invited you to their team yet. Ask artists to add your email as a {getRoleDisplayName().toLowerCase()} to their team first.
              </div>
              {!showManualInput ? (
                <button
                  onClick={() => setShowManualInput(true)}
                  className="mt-3 text-[#FF2300] text-[10px] underline hover:text-[#FF4520] transition-colors"
                >
                  Try a different email
                </button>
              ) : (
                <div className="mt-3 p-3 border border-white/20 rounded">
                  <div className="text-white text-[10px] mb-2">Enter the email that was invited:</div>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1 bg-black text-white text-[10px] px-2 py-1 rounded border border-white/20 focus:border-[#FF2300] outline-none"
                    />
                    <button
                      onClick={checkManualEmail}
                      disabled={loading || !manualEmail.trim()}
                      className="bg-[#FF2300] text-white text-[10px] px-3 py-1 rounded hover:bg-[#FF4520] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? 'Checking...' : 'Check'}
                    </button>
                  </div>
                  <button
                    onClick={() => {setShowManualInput(false); setManualEmail('');}}
                    className="mt-2 text-white/40 text-[9px] underline hover:text-white/60 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </SearchSection>

        <ButtonContainer>
          {availableArtists.length > 0 && (
            <ActionButton
              onClick={handleJoinArtist}
              className={`mr-[43px] ${selectedArtists.length > 0 && !loading ? "font-default" : "font-gray"}`}
            >
              {loading ? "Joining..." : selectedArtists.length > 1 ? "Join Teams" : "Join Team"}
            </ActionButton>
          )}
          <ActionButton className="cursor-pointer" onClick={() => setStep(15)}>Go back</ActionButton>
        </ButtonContainer>
      </FormSection>
      
      <Footer>
        <LogoImage src={dizeeLogo} alt="DIZEE" />
        <FooterText>
          {selectedArtists.length > 0 ? (
            `You're about to join ${selectedArtists.map(artist => artist.name).join(', ')}'s team${selectedArtists.length > 1 ? 's' : ''} as their ${getRoleDisplayName().toLowerCase()}. You'll have access to help manage their Dizee account and grow their presence.`
          ) : (
            `Select ${isProPlan ? 'one or more artists' : 'an artist'} whose team you'd like to join as a ${getRoleDisplayName().toLowerCase()}. ${isProPlan ? 'With your Pro plan, you can join multiple teams.' : 'With your Basic plan, you can join one team.'} You can only join teams where the artist has previously invited your email address.`
          )}
        </FooterText>
      </Footer>
      <AccessibilityWidget />
    </Container>
  );
};

export default Step16; 